/* @refresh reset */
import { FormikProvider, useFormik } from "formik";
import { Budgetlabel, IMaintenance, emptyBudgetLabel, emptyMaintenance } from "../../../../types/maintenance.type";
import * as Yup from 'yup';
import { useContext, useState } from "react";
import ButtonWithLoader from "../../../../common/components/ui/loading/ButtonLoading";
import { updateBudgetMaintenance } from "../../../../common/services/maintenance.api";
import TextareaFormik from "../../../renters/components/forms/TextareaFormik";
import { AuthContext } from '../../../../common/components/auth/authContext/AuthContext';
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import ItemRow from "./generate-pdfs/Itemrow";
import { HiTrash } from "react-icons/hi2";
import { ImCheckboxChecked, ImCheckboxUnchecked } from "react-icons/im";

type FormPropsEditBudgetMaintenance = {
  onClose: (() => void) | undefined;
  data: IMaintenance;
};

function FormBudgetMaintenance(props: FormPropsEditBudgetMaintenance,) {
  const authCtx = useContext(AuthContext);

  const budgetMaintenance = props.data.budgetMaintenance;
  const _itens = budgetMaintenance.corrective.concat(budgetMaintenance.misuse).concat(budgetMaintenance.preventive);
  const [itens, setItens] = useState(_itens.length > 0 ? _itens : [emptyBudgetLabel()]);
  const { notifySuccess } = useToastContext();
  const [diagnosis, setDiagnosis] = useState(props.data.diagnosis || false);

  const createSchema = (itens: Budgetlabel[]) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const schema: Record<string, any> = {};

    itens.forEach((_, index) => {
      schema[`name${index}`] = Yup.mixed().when("diagnosis", {
        is: (val: boolean) => val === false,
        then: (s) =>
          s.test(
            "has-name",
            "Item é obrigatório",
            (value) => !!(itens[index].name || value)
          ),
        otherwise: (s) => s.notRequired(),
      });

      schema[`quantity${index}`] = Yup.number().when("diagnosis", {
        is: (val: boolean) => val === false,
        then: (s) =>
          s.typeError("Quantidade deve ser número")
            .moreThan(0, "Quantidade insuficiente"),
        otherwise: (s) => s.notRequired(),
      });

      schema[`type${index}`] = Yup.string().when("diagnosis", {
        is: (val: boolean) => val === false,
        then: (s) => s.required("Tipo é obrigatório"),
        otherwise: (s) => s.notRequired(),
      });
    });

    return Yup.object().shape({
      diagnosis: Yup.boolean(),
      observation: Yup.string().nullable(),
      ...schema,
    });
  };

  const formatData = (data) => {

    const corrective: Budgetlabel[] = [];
    const misuse: Budgetlabel[] = [];
    const preventive: Budgetlabel[] = [];

    Array(itens.length).fill(null).map((_, index) => {

      const item = data[`name${index}`].value || data[`name${index}`];

      const name = item.name;
      const code = item.code;
      const productOrWorkId = item.productOrWorkId;
      const quantity = data[`quantity${index}`];
      const sellPrice = item.sellPrice;
      const type = data[`type${index}`];
      const outsourcedService = data[`outsourcedService${index}`] || item.outsourcedService;

      const budgetLabel = { name, code, productOrWorkId, quantity, sellPrice, type, outsourcedService };

      if (data[`type${index}`] === 'Corretiva') {
        corrective.push(budgetLabel)
      }
      if (data[`type${index}`] === 'misuse') {
        misuse.push(budgetLabel);
      }
      if (data[`type${index}`] === 'Preventiva') {
        preventive.push(budgetLabel);
      }
    });

    return { _id: props.data._id as string, budgetMaintenance: { corrective, misuse, preventive }, observation: { text: data.observation as string, author: authCtx.auth?.user?.email as string }, diagnosis: data.diagnosis };
  }

  const formik = useFormik({
    initialValues: {
      _id: props.data._id,
      observation: props.data.observations[0]?.text || "",
      budgetMaintenance: (props.data?.budgetMaintenance || emptyMaintenance().budgetMaintenance)
    },
    validationSchema: createSchema(itens),
    onSubmit: async (formikData) => {

      const finalizeForm = () => {
        props.onClose && props.onClose();
        notifySuccess("Atualizado com Sucesso");
      }

      const request = { ...formikData, budgetMaintenance: diagnosis ? emptyMaintenance().budgetMaintenance : formikData.budgetMaintenance, diagnosis };
      let data;
      if (diagnosis) {
        Object.keys(formik.values).forEach(key => {
          if (key.includes('name') || key.includes('quantity') || key.includes('type') || key.includes('outsourcedService')) {
            delete request[key];
          }
        });
        data = { ...request, observation: { text: request.observation as string, author: authCtx.auth?.user?.email as string } };
      } else {
        data = formatData(request);
      }

      await updateBudgetMaintenance(data);
      finalizeForm();
    }
  });

  const addItem = () => {
    setItens([...itens, emptyBudgetLabel()]);
  }

  return (
    <FormikProvider value={formik}>
      <div>
        <section className="grid grid-cols-1">
          <div className="relative color flex items-center">
            <label htmlFor={`diagnosis`} className="block mb-6 text-gray-600">
              {diagnosis ? (
                <div className="flex gap-2 items-center justify-start text-greenPrimary">
                  <ImCheckboxChecked />
                  <span>Diagnóstico ?</span>
                </div>
              ) : (
                <div className="flex gap-2 items-center justify-start">
                  <ImCheckboxUnchecked />
                  <span>Diagnóstico ?</span>
                </div>
              )}
              <input
                id="diagnosis"
                name="diagnosis"
                type="checkbox"
                checked={diagnosis}
                onChange={() => {
                  const newValue = !diagnosis;
                  setDiagnosis(newValue);
                  formik.setFieldValue('diagnosis', newValue);
                }}
                className="border p-2 rounded w-full absolute opacity-0"
              />
            </label>
          </div>
          {/* Desktop/table layout */}
          {!diagnosis && <div className="hidden md:block">
            <h3 className="text-lg font-normal mb-2">Peças trocadas</h3>
            <table className="min-w-[700px] w-full text-sm">
              <thead className="bg-gray-100 text-left text-xs md:text-sm">
                <tr>
                  <th className="p-2 border w-1/3">Item <span className="text-red-500">*</span></th>
                  <th className="p-2 border w-1/6">Quantidade <span className="text-red-500">*</span></th>
                  <th className="p-2 border w-1/4">Tipo <span className="text-red-500">*</span></th>
                  <th className="p-2 border w-1/4">Serviço</th>
                  <th className="p-2 border w-[40px] text-center">Remover</th>
                </tr>
              </thead>
              <tbody>
                {itens.map((item, index) => (
                  <tr key={`${item.code || 'item'}-${index}`}>
                    <ItemRow index={index} item={item} onRemoveClick={() => setItens(itens.filter((_, i) => i !== index))} />
                    <td className="p-2 border">
                      <button
                        type="button"
                        onClick={() => setItens(itens.filter((_, i) => i !== index))}
                        className="text-red-600 hover:text-red-800 text-xl"
                      >
                        <HiTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}

          {/* Mobile layout */}
          <div className="md:hidden flex flex-col gap-4">
            {itens.map((item, index) => (
              <details
                key={`${item.code || 'item'}-${index}`}
                className="border rounded shadow bg-white"
                open
              >
                <summary className="cursor-pointer px-4 py-2 font-medium bg-gray-100">
                  {item.name || `Item ${index + 1}`}
                </summary>
                <div className="p-4 grid gap-3">
                  <ItemRow
                    index={index}
                    item={item}
                    onRemoveClick={() => setItens(itens.filter((_, i) => i !== index))}
                    mode="mobile" // Novo prop
                  />
                  <button
                    type="button"
                    onClick={() => setItens(itens.filter((_, i) => i !== index))}
                    className="text-red-600 hover:text-red-800 flex items-center gap-1 text-sm"
                  >
                    <HiTrash />
                    Remover
                  </button>
                </div>
              </details>
            ))}
          </div>


          {!diagnosis && <div className="mt-4">
            <button type="button" onClick={addItem} className="text-blue-600">
              + Adicionar nova peça
            </button>
          </div>}
        </section>
        <section>
          <div className="w-full dark:text-black bg-gray/40 rounded my-4 p-3">
            <h2 className="font-semibold text-2xl mb-4">Observações</h2>
            <div>
              <TextareaFormik
                label="Observação"
                name="observation"
                className="min-h-64 w-full rounded shadow-md focus:border-white"
              />
            </div>
          </div>
        </section>
        <div className="text-end">
          <ButtonWithLoader
            isLoading={formik.isSubmitting}
            onClick={formik.handleSubmit}
            disabled={Object.keys(formik.errors).length > 0}
          >
            Salvar
          </ButtonWithLoader>
        </div>
      </div>
    </FormikProvider>
  );
}

export default FormBudgetMaintenance;
